import { ReactElement } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Flex, Text } from "@jsq/ds/core";
import { nodeEdgeOffset, TreeSection, NodeWrapper, nodeMaxWidth } from "./style_vanilla.css";
import { validatePosition, getNodeCSSVars } from "./util";
import type { TreeConfig, TreeNodeData, ElementRegistry } from "./types";

type TreeNodeProps = {
  treeNodes: Array<TreeNodeData>;
  treeConfig: TreeConfig;
  level: number;
  instanceId: string;
  elementRegistry: ElementRegistry;
};

export const TreeNode = ({
  treeNodes,
  treeConfig,
  level,
  instanceId,
  elementRegistry,
}: TreeNodeProps): ReactElement => {
  const configs = validatePosition(treeConfig);
  const cssVars = getNodeCSSVars(configs, level);
  return (
    <div role={level === 0 ? "tree" : "group"}>
      {treeNodes.map((node) => (
        <div
          key={node.id}
          role="treeitem"
          aria-level={level + 1}
          aria-expanded={Boolean(node.leafNodes?.length)}
          aria-selected="false"
        >
          <Flex
            ref={(el: HTMLDivElement | null) => {
              const key = `elem-${instanceId}-${node.id}`;
              if (el) elementRegistry.set(key, el);
              else elementRegistry.delete(key);
            }}
            data-testid={`elem-${node.id}`}
            data-level={level}
            className={TreeSection}
            style={assignInlineVars({
              [nodeEdgeOffset]: cssVars.edgeOffset,
              [nodeMaxWidth]: cssVars.maxWidth,
            })}
          >
            {typeof node.content === "string" ? (
              <Flex
                flexDirection="column"
                className={NodeWrapper}
                data-testid={`node-content-${node.id}`}
              >
                <Text variant="strong-lg">{node.content}</Text>
              </Flex>
            ) : (
              node.content
            )}
          </Flex>
          {node.leafNodes && (
            <TreeNode
              treeNodes={node.leafNodes}
              level={level + 1}
              treeConfig={treeConfig}
              instanceId={instanceId}
              elementRegistry={elementRegistry}
            />
          )}
        </div>
      ))}
    </div>
  );
};
